import React, { useState, useEffect } from 'react';
import './Admin.css';
import { validatePassword, getPasswordStrength } from '../utils/passwordValidation';
import ProjectManager from './ProjectManager';

function Admin({ users, projects, onAddUser, onDeleteUser, onUpdateUserRole, onChangePassword, onUpdateUser, onAddProject, onDeleteProject, currentUser }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user',
    projects: []
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordStrength, setPasswordStrength] = useState({ strength: 0, label: '' });
  
  // Stan dla modala zmiany hasła
  const [changePasswordModal, setChangePasswordModal] = useState(null);
  const [changePasswordData, setChangePasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [changePasswordErrors, setChangePasswordErrors] = useState([]);
  const [changePasswordStrength, setChangePasswordStrength] = useState({ strength: 0, label: '' });
  
  // Stan dla modala edycji użytkownika
  const [editUserModal, setEditUserModal] = useState(null);
  const [editUserData, setEditUserData] = useState({
    name: '',
    email: '',
    role: 'user',
    projects: []
  });

  // Stan dla modala dodawania projektu
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [addProjectContext, setAddProjectContext] = useState(null); // 'add' lub 'edit'

  // Stan dla modala potwierdzenia usunięcia użytkownika
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [deletingUserEmail, setDeletingUserEmail] = useState(null);

  // Funkcja do sortowania użytkowników według ról
  const sortUsersByRole = (users) => {
    const roleOrder = {
      'owner': 1,
      'admin': 2,
      'lider': 3,
      'user': 4,
      'guest': 5
    };
    
    return [...users].sort((a, b) => {
      const roleA = roleOrder[a.role] || 99; // Domyślnie na końcu, jeśli rola nieznana
      const roleB = roleOrder[b.role] || 99;
      
      if (roleA !== roleB) {
        return roleA - roleB;
      }
      
      // Jeśli role są takie same, sortuj alfabetycznie po imieniu
      return (a.name || '').localeCompare(b.name || '');
    });
  };


  const handleChange = (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
    setError('');
    setSuccess('');
    
    // Walidacja hasła w czasie rzeczywistym
    if (e.target.name === 'password') {
      const validation = validatePassword(value);
      setPasswordErrors(validation.errors);
      const strength = getPasswordStrength(value);
      setPasswordStrength(strength);
    }
    
    // Obsługa checkboxów dla projektów
    if (e.target.name === 'project' && e.target.type === 'checkbox') {
      const projectName = e.target.value;
      const isChecked = e.target.checked;
      setFormData({
        ...formData,
        projects: isChecked
          ? [...(formData.projects || []), projectName]
          : (formData.projects || []).filter(p => p !== projectName)
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.name) {
      setError('Proszę wypełnić wszystkie pola');
      setSuccess('');
      return;
    }

    // Walidacja hasła
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setError('Hasło nie spełnia wymagań bezpieczeństwa');
      setPasswordErrors(passwordValidation.errors);
      setSuccess('');
      return;
    }

    // Sprawdź czy użytkownik już istnieje
    if (users.some(u => u.email && u.email.toLowerCase() === formData.email.toLowerCase())) {
      setError('Użytkownik o tym adresie email już istnieje');
      setSuccess('');
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      await onAddUser({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: formData.role,
        projects: formData.projects || []
      });

      setSuccess('Użytkownik został dodany pomyślnie!');
      setFormData({
        email: '',
        password: '',
        name: '',
        role: 'user',
        projects: []
      });
      setPasswordErrors([]);
      setPasswordStrength({ strength: 0, label: '' });
    } catch (err) {
      console.error('Błąd dodawania użytkownika:', err);
      setError(err.message || 'Nie udało się dodać użytkownika. Sprawdź połączenie z serwerem.');
      setSuccess('');
    }
  };

  const handleDelete = (email) => {
    if (email === currentUser?.email) {
      setError('Nie możesz usunąć własnego konta');
      return;
    }
    setDeletingUserEmail(email);
    setShowDeleteUserModal(true);
    setError('');
  };

  const handleDeleteUserConfirm = () => {
    if (deletingUserEmail) {
      onDeleteUser(deletingUserEmail);
      setSuccess('Użytkownik został usunięty');
      setShowDeleteUserModal(false);
      setDeletingUserEmail(null);
    }
  };

  const handleDeleteUserCancel = () => {
    setShowDeleteUserModal(false);
    setDeletingUserEmail(null);
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'owner': 'Owner',
      'admin': 'Admin',
      'lider': 'Lider',
      'user': 'Użytkownik',
      'guest': 'Gość'
    };
    return roleNames[role] || role;
  };

  const canChangeRole = (userEmail, userRole, newRole) => {
    // Owner nie może odebrać sobie uprawnień
    if (userEmail === currentUser?.email && currentUser?.role === 'owner' && newRole !== 'owner') {
      return false;
    }
    // Admin nie może odebrać sobie uprawnień (chyba że do owner)
    if (userEmail === currentUser?.email && currentUser?.role === 'admin' && newRole !== 'admin' && newRole !== 'owner') {
      return false;
    }
    return true;
  };

  const handleRoleChange = (email, newRole) => {
    const user = users.find(u => u.email === email);
    if (!canChangeRole(email, user?.role, newRole)) {
      setError('Nie możesz odebrać sobie uprawnień');
      return;
    }
    onUpdateUserRole(email, newRole);
    setSuccess('Uprawnienia zostały zaktualizowane');
  };

  const openChangePasswordModal = (userEmail, userName) => {
    setChangePasswordModal({ email: userEmail, name: userName });
    setChangePasswordData({ password: '', confirmPassword: '' });
    setChangePasswordErrors([]);
    setChangePasswordStrength({ strength: 0, label: '' });
    setError('');
  };

  const closeChangePasswordModal = () => {
    setChangePasswordModal(null);
    setChangePasswordData({ password: '', confirmPassword: '' });
    setChangePasswordErrors([]);
    setChangePasswordStrength({ strength: 0, label: '' });
  };

  const handleChangePasswordInput = (e) => {
    const value = e.target.value;
    const field = e.target.name;
    
    setChangePasswordData({
      ...changePasswordData,
      [field]: value
    });
    
    setError('');
    
    if (field === 'password') {
      const validation = validatePassword(value);
      setChangePasswordErrors(validation.errors);
      const strength = getPasswordStrength(value);
      setChangePasswordStrength(strength);
    }
  };

  const handleSubmitPasswordChange = (e) => {
    e.preventDefault();
    
    if (!changePasswordData.password || !changePasswordData.confirmPassword) {
      setError('Proszę wypełnić wszystkie pola');
      return;
    }

    if (changePasswordData.password !== changePasswordData.confirmPassword) {
      setError('Hasła nie są identyczne');
      return;
    }

    const passwordValidation = validatePassword(changePasswordData.password);
    if (!passwordValidation.isValid) {
      setError('Hasło nie spełnia wymagań bezpieczeństwa');
      setChangePasswordErrors(passwordValidation.errors);
      return;
    }

    onChangePassword(changePasswordModal.email, changePasswordData.password);
    setSuccess(`Hasło dla użytkownika ${changePasswordModal.name} zostało zmienione`);
    closeChangePasswordModal();
  };

  const isSuperAdmin = currentUser?.role === 'owner';

  // Funkcja sprawdzająca, czy można edytować użytkownika
  const canEditUser = (user) => {
    const currentRole = currentUser?.role;
    const targetRole = user?.role;
    
    if (currentRole === 'owner') {
      // Owner może edytować wszystkich
      return true;
    } else if (currentRole === 'admin') {
      // Admin może edytować tylko Lidera, Użytkownika i Gościa
      return targetRole === 'lider' || targetRole === 'user' || targetRole === 'guest';
    }
    // Lider i Użytkownik nie mogą edytować
    return false;
  };

  // Funkcja sprawdzająca, czy można zmienić hasło użytkownikowi
  const canChangePasswordForUser = (user) => {
    const currentRole = currentUser?.role;
    const targetRole = user?.role;
    
    if (currentRole === 'owner') {
      // Owner może zmienić hasło wszystkim
      return true;
    } else if (currentRole === 'admin') {
      // Admin może zmienić hasło tylko Liderowi, Użytkownikowi i Gościowi
      return targetRole === 'lider' || targetRole === 'user' || targetRole === 'guest';
    }
    // Lider i Użytkownik nie mogą zmieniać hasła
    return false;
  };

  // Funkcja zwracająca dostępne role w zależności od roli zalogowanego użytkownika
  const getAvailableRoles = () => {
    const userRole = currentUser?.role;
    
    if (userRole === 'owner') {
      return [
        { value: 'owner', label: 'Owner' },
        { value: 'admin', label: 'Admin' },
        { value: 'lider', label: 'Lider' },
        { value: 'user', label: 'Użytkownik' },
        { value: 'guest', label: 'Gość' }
      ];
    } else if (userRole === 'admin') {
      return [
        { value: 'lider', label: 'Lider' },
        { value: 'user', label: 'Użytkownik' },
        { value: 'guest', label: 'Gość' }
      ];
    } else {
      // Lider i Użytkownik nie mogą dodawać kont
      return [];
    }
  };

  // Funkcje do obsługi modala dodawania projektu
  const handleAddProjectConfirm = () => {
    if (!newProjectName || !newProjectName.trim()) {
      setError('Proszę podać nazwę klienta');
      return;
    }

    const trimmedName = newProjectName.trim();
    if (projects.includes(trimmedName)) {
      setError('Klient o tej nazwie już istnieje');
      return;
    }

    onAddProject(trimmedName);
    
    // Dodaj projekt do odpowiedniego kontekstu
    if (addProjectContext === 'add') {
      setFormData({ 
        ...formData, 
        projects: [...(formData.projects || []), trimmedName] 
      });
    } else if (addProjectContext === 'edit') {
      setEditUserData({ 
        ...editUserData, 
        projects: [...editUserData.projects, trimmedName] 
      });
    }

    setSuccess(`Klient "${trimmedName}" został dodany i wybrany`);
    setShowAddProjectModal(false);
    setNewProjectName('');
    setAddProjectContext(null);
    setError('');
  };

  const handleAddProjectCancel = () => {
    setShowAddProjectModal(false);
    setNewProjectName('');
    setAddProjectContext(null);
    setError('');
  };

  // Funkcje do zarządzania logami

  const canAssignProjects = isSuperAdmin || currentUser?.role === 'admin' || currentUser?.role === 'lider';
  
  // Projekty dostępne dla danego użytkownika
  const getAvailableProjects = () => {
    if (isSuperAdmin) {
      return projects; // Owner widzi wszystkie projekty
    }
    // Admin i Lider widzą wszystkie projekty (można później dodać logikę filtrowania)
    return projects;
  };

  const openEditUserModal = (user) => {
    setEditUserModal(user.email);
    setEditUserData({
      name: user.name,
      email: user.email || '',
      role: user.role || 'user',
      projects: user.projects || []
    });
    setError('');
  };

  const closeEditUserModal = () => {
    setEditUserModal(null);
    setEditUserData({
      name: '',
      email: '',
      role: 'user',
      projects: []
    });
  };

  const handleEditUserChange = (e) => {
    const value = e.target.value;
    const name = e.target.name;
    
    if (e.target.type === 'checkbox') {
      const projectName = e.target.value;
      const isChecked = e.target.checked;
      setEditUserData({
        ...editUserData,
        projects: isChecked
          ? [...editUserData.projects, projectName]
          : editUserData.projects.filter(p => p !== projectName)
      });
    } else {
      setEditUserData({
        ...editUserData,
        [name]: value
      });
    }
    setError('');
  };

  const handleSubmitEditUser = (e) => {
    e.preventDefault();
    
    if (!editUserData.name) {
      setError('Imię i nazwisko jest wymagane');
      return;
    }

    if (!editUserData.email) {
      setError('Adres email jest wymagany');
      return;
    }

    // Walidacja formatu email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editUserData.email)) {
      setError('Nieprawidłowy format adresu email');
      return;
    }

    // Sprawdź czy email nie jest już używany przez innego użytkownika
    const existingUser = users.find(u => 
      u.email && 
      u.email.toLowerCase() === editUserData.email.toLowerCase() && 
      u.email !== editUserModal
    );
    
    if (existingUser) {
      setError('Adres email jest już używany przez innego użytkownika');
      return;
    }

    onUpdateUser(editUserModal, {
      name: editUserData.name,
      email: editUserData.email,
      role: editUserData.role,
      projects: editUserData.projects
    });

    setSuccess(`Użytkownik ${editUserData.name} został zaktualizowany`);
    closeEditUserModal();
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>Panel Administracyjny</h2>
        <p>Zarządzanie użytkownikami i uprawnieniami</p>
        <div className="admin-header-actions">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'admin-permissions' }))}
            className="permissions-button"
            title="Zarządzaj uprawnieniami użytkowników"
          >
            🔐 Zarządzaj Uprawnieniami
          </button>
        </div>
      </div>

      {error && <div className="admin-message error">{error}</div>}
      {success && <div className="admin-message success">{success}</div>}

      <div className="admin-content">
        {getAvailableRoles().length > 0 && (
          <div className="admin-section">
            <h3>Dodaj nowego użytkownika</h3>
            <form onSubmit={handleSubmit} className="add-user-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Imię i nazwisko:</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Jan Kowalski"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email:</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="jan.kowalski@mcg.pl"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password">Hasło:</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  required
                />
                {formData.password && (
                  <div className="password-requirements">
                    <div className="password-strength">
                      <span className="strength-label">Siła hasła: </span>
                      <span className={`strength-value strength-${passwordStrength.strength}`}>
                        {passwordStrength.label || 'Brak'}
                      </span>
                    </div>
                    {passwordErrors.length > 0 && (
                      <ul className="password-errors">
                        {passwordErrors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    )}
                    {passwordErrors.length === 0 && formData.password && (
                      <div className="password-valid">✓ Hasło spełnia wszystkie wymagania</div>
                    )}
                  </div>
                )}
                <div className="password-hint">
                  <strong>Wymagania:</strong> min. 8 znaków, wielka i mała litera, cyfra, znak specjalny
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="role">Uprawnienia:</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  disabled={getAvailableRoles().length === 0}
                >
                  {getAvailableRoles().length === 0 ? (
                    <option value="">Brak uprawnień do dodawania kont</option>
                  ) : (
                    <>
                      {getAvailableRoles().map((role) => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>
            {canAssignProjects && (
              <div className="form-row">
                <div className="form-group projects-group">
                  <div className="projects-header">
                    <label htmlFor="projects-select">Klienci (można wybrać wielu):</label>
                    {isSuperAdmin && (
                      <button
                        type="button"
                        onClick={() => {
                          setNewProjectName('');
                          setAddProjectContext('add');
                          setShowAddProjectModal(true);
                          setError('');
                        }}
                        className="add-project-button"
                        title="Dodaj nowego klienta"
                      >
                        <span className="add-icon">+</span>
                        Nowy klient
                      </button>
                    )}
                  </div>
                  <div className="projects-container">
                    {getAvailableProjects().length === 0 ? (
                      <div className="no-projects-message">
                        <span className="no-projects-icon">📁</span>
                        <p className="no-projects-text">Brak dostępnych klientów</p>
                      </div>
                    ) : (
                      <div className="projects-grid">
                        {getAvailableProjects().map((project, idx) => (
                          <label key={idx} className="project-card">
                            <input
                              type="checkbox"
                              name="project"
                              value={project}
                              checked={(formData.projects || []).includes(project)}
                              onChange={handleChange}
                              className="project-checkbox"
                            />
                            <div className="project-card-content">
                              <span className="project-name">{project}</span>
                              <span className="project-check-icon">✓</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <button type="submit" className="add-user-button">Dodaj użytkownika</button>
          </form>
          </div>
        )}

        <div className="admin-section">
          <h3>Lista użytkowników ({users.filter(user => user.role !== 'employee' && user.role !== 'owner').length})</h3>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Imię i nazwisko</th>
                  <th>Email</th>
                  <th>Uprawnienia</th>
                  <th>Klient</th>
                  <th>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {sortUsersByRole(users.filter(user => user.role !== 'employee' && user.role !== 'owner')).map((user, index) => (
                  <tr key={index}>
                    <td>{user.name}</td>
                    <td>{user.email && !user.email.includes('_') ? user.email : 'Pracownik (brak konta)'}</td>
                    <td>
                      <div className="role-cell">
                        <select
                          value={user.role || 'user'}
                          onChange={(e) => handleRoleChange(user.email, e.target.value)}
                          className={`role-select role-${user.role || 'user'}`}
                          disabled={
                            (user.email === currentUser?.email && currentUser?.role === 'owner' && user.role === 'owner') ||
                            (user.email === currentUser?.email && currentUser?.role === 'admin' && user.role === 'admin') ||
                            getAvailableRoles().length === 0
                          }
                        >
                          {getAvailableRoles().length === 0 ? (
                            <option value={user.role || 'user'}>{getRoleDisplayName(user.role || 'user')}</option>
                          ) : (
                            <>
                              {getAvailableRoles().map((role) => (
                                <option key={role.value} value={role.value}>{role.label}</option>
                              ))}
                            </>
                          )}
                        </select>
                        <span className={`role-badge role-badge-${user.role || 'user'}`}>{getRoleDisplayName(user.role || 'user')}</span>
                      </div>
                    </td>
                    <td>
                      <div className="projects-badges">
                        {user.projects && user.projects.length > 0 ? (
                          user.projects.map((project, idx) => (
                            <span key={idx} className="project-badge">{project}</span>
                          ))
                        ) : (
                          <span className="project-badge no-project">Brak</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        {canEditUser(user) && (
                          <button
                            onClick={() => openEditUserModal(user)}
                            className="edit-button"
                            title="Edytuj użytkownika"
                          >
                            Edytuj
                          </button>
                        )}
                        {canChangePasswordForUser(user) && (
                          <button
                            onClick={() => openChangePasswordModal(user.email, user.name)}
                            className="change-password-button"
                            title="Zmień hasło użytkownika"
                          >
                            Hasło
                          </button>
                        )}
                        {isSuperAdmin && (
                          <button
                            onClick={() => handleDelete(user.email)}
                            className="delete-button"
                            disabled={user.email === currentUser?.email}
                            title={user.email === currentUser?.email ? 'Nie możesz usunąć własnego konta' : 'Usuń użytkownika'}
                          >
                            Usuń
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Zarządzanie projektami - tylko dla owner */}
        {isSuperAdmin && (
          <div className="admin-section">
            <h3>Zarządzanie klientami</h3>
            <ProjectManager 
              projects={projects}
              onAddProject={onAddProject}
              onDeleteProject={onDeleteProject}
            />
          </div>
        )}

      </div>

      {/* Modal zmiany hasła */}
      {changePasswordModal && (
        <div className="modal-overlay" onClick={closeChangePasswordModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Zmień hasło dla: {changePasswordModal.name}</h3>
              <button className="modal-close" onClick={closeChangePasswordModal}>×</button>
            </div>
            <form onSubmit={handleSubmitPasswordChange} className="change-password-form">
              <div className="form-group">
                <label htmlFor="new-password">Nowe hasło:</label>
                <input
                  type="password"
                  id="new-password"
                  name="password"
                  value={changePasswordData.password}
                  onChange={handleChangePasswordInput}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  required
                />
                {changePasswordData.password && (
                  <div className="password-requirements">
                    <div className="password-strength">
                      <span className="strength-label">Siła hasła: </span>
                      <span className={`strength-value strength-${changePasswordStrength.strength}`}>
                        {changePasswordStrength.label || 'Brak'}
                      </span>
                    </div>
                    {changePasswordErrors.length > 0 && (
                      <ul className="password-errors">
                        {changePasswordErrors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    )}
                    {changePasswordErrors.length === 0 && changePasswordData.password && (
                      <div className="password-valid">✓ Hasło spełnia wszystkie wymagania</div>
                    )}
                  </div>
                )}
                <div className="password-hint">
                  <strong>Wymagania:</strong> min. 8 znaków, wielka i mała litera, cyfra, znak specjalny
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="confirm-password">Potwierdź hasło:</label>
                <input
                  type="password"
                  id="confirm-password"
                  name="confirmPassword"
                  value={changePasswordData.confirmPassword}
                  onChange={handleChangePasswordInput}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  required
                />
                {changePasswordData.confirmPassword && 
                 changePasswordData.password !== changePasswordData.confirmPassword && (
                  <div className="password-error">Hasła nie są identyczne</div>
                )}
                {changePasswordData.confirmPassword && 
                 changePasswordData.password === changePasswordData.confirmPassword &&
                 changePasswordErrors.length === 0 && (
                  <div className="password-valid">✓ Hasła są identyczne</div>
                )}
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="modal-actions">
                <button type="button" onClick={closeChangePasswordModal} className="cancel-button">
                  Anuluj
                </button>
                <button type="submit" className="submit-button">
                  Zmień hasło
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal edycji użytkownika */}
      {editUserModal && (
        <div className="modal-overlay" onClick={closeEditUserModal}>
          <div className="modal-content edit-user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edytuj użytkownika: {users.find(u => u.email === editUserModal)?.email}</h3>
              <button className="modal-close" onClick={closeEditUserModal}>×</button>
            </div>
            <form onSubmit={handleSubmitEditUser} className="edit-user-form">
              <div className="form-group">
                <label htmlFor="edit-name">Imię i nazwisko:</label>
                <input
                  type="text"
                  id="edit-name"
                  name="name"
                  value={editUserData.name}
                  onChange={handleEditUserChange}
                  placeholder="Jan Kowalski"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-email">Adres email:</label>
                <input
                  type="email"
                  id="edit-email"
                  name="email"
                  value={editUserData.email}
                  onChange={handleEditUserChange}
                  placeholder="jan.kowalski@mcg.pl"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-role">Uprawnienia:</label>
                <select
                  id="edit-role"
                  name="role"
                  value={editUserData.role}
                  onChange={handleEditUserChange}
                  required
                  disabled={getAvailableRoles().length === 0}
                >
                  {getAvailableRoles().length === 0 ? (
                    <option value={editUserData.role}>{getRoleDisplayName(editUserData.role)}</option>
                  ) : (
                    <>
                      {getAvailableRoles().map((role) => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>
              {canAssignProjects && (
                <div className="form-group projects-group">
                  <div className="projects-header">
                    <label htmlFor="edit-projects-select">Projekty (można wybrać wiele):</label>
                    {isSuperAdmin && (
                      <button
                        type="button"
                        onClick={() => {
                          setNewProjectName('');
                          setAddProjectContext('edit');
                          setShowAddProjectModal(true);
                          setError('');
                        }}
                        className="add-project-button"
                        title="Dodaj nowego klienta"
                      >
                        <span className="add-icon">+</span>
                        Nowy klient
                      </button>
                    )}
                  </div>
                  <div className="projects-container">
                    {getAvailableProjects().length === 0 ? (
                      <div className="no-projects-message">
                        <span className="no-projects-icon">📁</span>
                        <p className="no-projects-text">Brak dostępnych klientów</p>
                      </div>
                    ) : (
                      <div className="projects-grid">
                        {getAvailableProjects().map((project, idx) => (
                          <label key={idx} className="project-card">
                            <input
                              type="checkbox"
                              name="project"
                              value={project}
                              checked={(editUserData.projects || []).includes(project)}
                              onChange={handleEditUserChange}
                              className="project-checkbox"
                            />
                            <div className="project-card-content">
                              <span className="project-name">{project}</span>
                              <span className="project-check-icon">✓</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {error && <div className="error-message">{error}</div>}
              <div className="modal-actions">
                <button type="button" onClick={closeEditUserModal} className="cancel-button">
                  Anuluj
                </button>
                <button type="submit" className="submit-button">
                  Zapisz zmiany
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal dla dodawania nowego projektu */}
      {showAddProjectModal && (
        <div className="modal-overlay" onClick={handleAddProjectCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ padding: '32px' }}>
            <h3>Dodaj nowego klienta</h3>
            {error && <div className="admin-message error">{error}</div>}
            <div className="modal-form-group">
              <label htmlFor="new-project-name">Nazwa klienta:</label>
              <input
                type="text"
                id="new-project-name"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="np. Nowy Klient"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddProjectConfirm();
                  } else if (e.key === 'Escape') {
                    handleAddProjectCancel();
                  }
                }}
                autoFocus
              />
            </div>
            <div className="modal-actions">
              <button 
                type="button"
                className="btn-cancel"
                onClick={handleAddProjectCancel}
              >
                Anuluj
              </button>
              <button 
                type="button"
                className="btn-confirm"
                onClick={handleAddProjectConfirm}
              >
                Dodaj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal dla potwierdzenia usunięcia użytkownika */}
      {showDeleteUserModal && deletingUserEmail && (
        <div className="modal-overlay" onClick={handleDeleteUserCancel}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Potwierdź usunięcie</h3>
            <p>
              Czy na pewno chcesz usunąć użytkownika{' '}
              <strong>
                {users.find(u => u.email === deletingUserEmail)?.name || deletingUserEmail}
              </strong>?
            </p>
            <p className="delete-warning">Ta operacja jest nieodwracalna.</p>
            <div className="modal-actions">
              <button 
                type="button"
                className="btn-cancel"
                onClick={handleDeleteUserCancel}
              >
                Anuluj
              </button>
              <button 
                type="button"
                className="btn-delete"
                onClick={handleDeleteUserConfirm}
              >
                Usuń
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;

